/**
 * Monopool & bewustzijnskristallen-laag op de bodygraph (Misc-tab → Bodygraph).
 *
 * Bouwt de SVG-markup voor een pure sier-laag bovenop de geladen bodygraph:
 * - het magnetisch-monopool-icoon in het midden van het G-center, waar het
 *   geen poortnummers bedekt; de hover-tooltip benoemt de eigenlijke zetel
 *   (poort 2). Opbouw: geel vierkant, centraal zwart blokje met een ring van
 *   8 gele stippen, haarlijntjes naar hoeken en randmiddens, vullende
 *   hoekdriehoeken en kleine dubbel-driehoekjes op de randmiddens —
 *   nagetekend van Stevens referentie-afbeelding;
 * - een subtiele "spies" van licht die vanaf het monopool omhoog loopt en
 *   naar boven toe uitfadet (smal bij het monopool, breder richting hoofd);
 * - het design-kristal (rood, gefacetteerd) in het midden van de ajna;
 * - het personality-kristal (zwart, gefacetteerd) op het bovenste puntje
 *   van het hoofd-center.
 *
 * De laag leeft in viewBox-coördinaten van de bodygraph-SVG zelf en wordt
 * door {@link HdBodygraphChartComponent} als laatste child geïnjecteerd
 * (bovenop alle centers/kanalen), met `pointer-events: none` zodat het
 * edit-klikgedrag van poorten onaangetast blijft. De geometrie komt uit
 * runtime-metingen van de center-shapes (zelfde CTM-aanpak als de
 * gate-anchors), zodat de laag meebeweegt met het SVG-asset.
 */

export interface HdMonopoleLayerRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface HdMonopoleLayerGeometry {
  /** Union-bbox van het G-center (zelf-shape) in viewBox-coördinaten. */
  readonly gCenter: HdMonopoleLayerRect;
  /** Union-bbox van het hoofd-center (hoofd-shape). */
  readonly head: HdMonopoleLayerRect;
  /** Union-bbox van de ajna (ajna-shape). */
  readonly ajna: HdMonopoleLayerRect;
}

export const HD_MONOPOLE_LAYER_ID = 'hd-monopole-layer';

// Kleuren van het monopool-icoon: hetzelfde G-geel als de locks & keys
// lock-chips (rgb(253,244,103)). Ook de buitenrand is geel (r7): het zwarte
// patroon ligt als negatieve ruimte volledig ín het gele vlak.
const MONOPOLE_YELLOW = '#fdf467';
const MONOPOLE_BLACK = '#141414';

// Spies: het frisse zelf/G-center-geel (defined-fill rgb(234,244,121)), met
// een net donkerder geel voor de randlijn (finetune 2026-09-21: minder blur,
// minder fade, kleur richting het zelf-geel, subtiele border).
const BEAM_COLOR_BASE = '#e0e455';
const BEAM_COLOR_TIP = '#eaf479';
const BEAM_COLOR_EDGE = '#c2b93b';

interface HdCrystalPalette {
  readonly base: string;
  readonly highlight: string;
  readonly light: string;
  readonly dark: string;
  readonly deep: string;
  readonly outline: string;
}

// Personality-kristal: grafiet/zwart met koele facet-highlights.
const PERSONALITY_CRYSTAL_PALETTE: HdCrystalPalette = {
  base: '#2e2e36',
  highlight: '#6a6a78',
  light: '#494952',
  dark: '#1e1e26',
  deep: '#15151b',
  outline: '#101014',
};

// Design-kristal: koraalrood, zoals in de referentie.
const DESIGN_CRYSTAL_PALETTE: HdCrystalPalette = {
  base: '#c0392f',
  highlight: '#ef7f66',
  light: '#d9553f',
  dark: '#8e211c',
  deep: '#701a16',
  outline: '#5c120f',
};

function fmt(value: number): string {
  return Number(value.toFixed(2)).toString();
}

function points(coords: ReadonlyArray<readonly [number, number]>): string {
  return coords.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(' ');
}

/**
 * Eén dubbel-driehoek-pijl (met smalle spleet in het midden) die vanaf een
 * randmidden naar het centrum wijst. Getekend in lokale coördinaten voor de
 * "boven"-positie (basis op afstand `baseDistance` boven het midden, punt
 * omlaag); de aanroeper roteert de groep naar de juiste rand.
 */
function buildMonopoleArrow(side: number, baseDistance: number, halfWidth: number, depth: number, rotation: number): string {
  // De twee driehoekjes sluiten op elkaar aan óp de haarlijn: de spleet is
  // net zo smal als de halve haarlijndikte, zodat de lijn als naad zichtbaar
  // blijft (finetune 2026-09-21 r4).
  const gap = side * 0.008;
  const left = points([
    [-halfWidth, -baseDistance],
    [-gap, -baseDistance],
    [-gap, -baseDistance + depth],
  ]);
  const right = points([
    [halfWidth, -baseDistance],
    [gap, -baseDistance],
    [gap, -baseDistance + depth],
  ]);
  return `<g class="hd-monopole-arrow" transform="rotate(${fmt(rotation)})">`
    + `<polygon points="${left}" fill="${MONOPOLE_BLACK}"/>`
    + `<polygon points="${right}" fill="${MONOPOLE_BLACK}"/>`
    + `</g>`;
}

/**
 * Het monopool-icoon, gecentreerd op (cx, cy) met zijde `side`: geel vierkant,
 * haarlijntjes van het centrale blokje naar de vier hoeken én randmiddens,
 * kleine dubbel-driehoekjes op de randmiddens, één vullende driehoek in elke
 * hoek, en het zwarte blokje met een ring van 8 gele stippen (openingen op
 * 12, 3, 6 en 9 uur).
 */
function buildMonopoleIcon(cx: number, cy: number, side: number, uid: number): string {
  const half = side / 2;
  const clipId = `hd-monopole-icon-clip-${uid}`;

  const arrows: string[] = [];
  for (let index = 0; index < 4; index++) {
    // Slank langs de rand (r5): het paar is 0.21s breed bij 0.13s diepte.
    arrows.push(buildMonopoleArrow(side, half, side * 0.105, side * 0.13, index * 90));
  }

  // Eén massieve driehoek per hoek die de hoek van het vierkant vult
  // (rechte hoek op de vierkant-hoek, benen langs beide randen).
  const cornerLeg = side * 0.28;
  const cornerTriangles: string[] = [];
  for (const [sx, sy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as const) {
    cornerTriangles.push(
      `<polygon class="hd-monopole-corner-triangle" points="${points([
        [sx * half, sy * half],
        [sx * (half - cornerLeg), sy * half],
        [sx * half, sy * (half - cornerLeg)],
      ])}" fill="${MONOPOLE_BLACK}"/>`,
    );
  }

  // Dunne haarlijntjes van het centrale zwarte blokje naar de vier hoeken
  // én naar de vier randmiddens (achter de driehoekjes).
  const coreHalf = side * 0.2;
  const hairlines: string[] = [];
  for (const [sx, sy] of [[1, 1], [1, -1], [-1, 1], [-1, -1], [1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
    hairlines.push(
      `<line class="hd-monopole-hairline" x1="${fmt(sx * coreHalf)}" y1="${fmt(sy * coreHalf)}" x2="${fmt(sx * half)}" y2="${fmt(sy * half)}" stroke="${MONOPOLE_BLACK}" stroke-width="${fmt(side * 0.014)}"/>`,
    );
  }

  // 8 stippen met de openingen (niet de stippen) op 12, 3, 6 en 9 uur:
  // fase-offset van een halve stap (22,5°) t.o.v. de klok-assen.
  const dotRingRadius = side * 0.115;
  const dots: string[] = [];
  for (let index = 0; index < 8; index++) {
    const angle = (-67.5 + index * 45) * (Math.PI / 180);
    dots.push(
      `<circle class="hd-monopole-dot" cx="${fmt(dotRingRadius * Math.cos(angle))}" cy="${fmt(dotRingRadius * Math.sin(angle))}" r="${fmt(side * 0.026)}" fill="${MONOPOLE_YELLOW}"/>`,
    );
  }

  return `<g class="hd-monopole-icon" transform="translate(${fmt(cx)},${fmt(cy)})">`
    + `<clipPath id="${clipId}"><rect x="${fmt(-half)}" y="${fmt(-half)}" width="${fmt(side)}" height="${fmt(side)}" rx="${fmt(side * 0.03)}"/></clipPath>`
    + `<rect x="${fmt(-half)}" y="${fmt(-half)}" width="${fmt(side)}" height="${fmt(side)}" rx="${fmt(side * 0.03)}" fill="${MONOPOLE_YELLOW}"/>`
    + `<g clip-path="url(#${clipId})">${hairlines.join('')}${cornerTriangles.join('')}${arrows.join('')}</g>`
    // Gele kaderrand óver het patroon: de binnenhelft van de stroke houdt de
    // zwarte vormen van de rand af (negatieve ruimte), de buitenhelft maakt
    // het vierkant rondom net iets groter (r7).
    + `<rect class="hd-monopole-frame" x="${fmt(-half)}" y="${fmt(-half)}" width="${fmt(side)}" height="${fmt(side)}" rx="${fmt(side * 0.03)}" fill="none" stroke="${MONOPOLE_YELLOW}" stroke-width="${fmt(side * 0.055)}"/>`
    + `<rect class="hd-monopole-core" x="${fmt(-coreHalf)}" y="${fmt(-coreHalf)}" width="${fmt(coreHalf * 2)}" height="${fmt(coreHalf * 2)}" fill="${MONOPOLE_BLACK}"/>`
    + dots.join('')
    + `</g>`;
}

/**
 * Gefacetteerd kristal (zeshoekige gem-silhouet + facet-vlakken), gecentreerd
 * op (cx, cy). Zelfde opbouw voor beide kristallen; alleen het palet verschilt.
 */
function buildCrystal(cx: number, cy: number, width: number, height: number, palette: HdCrystalPalette, variant: string): string {
  const top: readonly [number, number] = [0, -height * 0.5];
  const upperLeft: readonly [number, number] = [-width * 0.5, -height * 0.16];
  const upperRight: readonly [number, number] = [width * 0.5, -height * 0.16];
  const lowerLeft: readonly [number, number] = [-width * 0.32, height * 0.24];
  const lowerRight: readonly [number, number] = [width * 0.32, height * 0.24];
  const bottom: readonly [number, number] = [0, height * 0.5];
  const centerLeft: readonly [number, number] = [-width * 0.06, -height * 0.02];
  const centerRight: readonly [number, number] = [width * 0.06, -height * 0.02];

  const outline = points([top, upperRight, lowerRight, bottom, lowerLeft, upperLeft]);
  const strokeWidth = Math.max(1, height * 0.03);

  return `<g class="hd-monopole-crystal hd-monopole-crystal--${variant}" transform="translate(${fmt(cx)},${fmt(cy)})">`
    + `<polygon points="${outline}" fill="${palette.base}"/>`
    + `<polygon points="${points([top, upperLeft, centerLeft])}" fill="${palette.highlight}"/>`
    + `<polygon points="${points([top, upperRight, centerRight])}" fill="${palette.light}"/>`
    + `<polygon points="${points([upperLeft, lowerLeft, centerLeft])}" fill="${palette.light}"/>`
    + `<polygon points="${points([upperRight, lowerRight, centerRight])}" fill="${palette.dark}"/>`
    + `<polygon points="${points([lowerLeft, bottom, centerLeft])}" fill="${palette.dark}"/>`
    + `<polygon points="${points([lowerRight, bottom, centerRight])}" fill="${palette.deep}"/>`
    + `<polygon points="${points([top, centerLeft, centerRight])}" fill="${palette.light}" opacity="0.55"/>`
    + `<polygon points="${outline}" fill="none" stroke="${palette.outline}" stroke-width="${fmt(strokeWidth)}" stroke-linejoin="round"/>`
    + `</g>`;
}

/**
 * Volledige laag-markup. `uid` maakt de def-ids (gradient/filter/clip) uniek
 * per component-instantie: `url(#…)`-referenties resolven document-breed en
 * er kunnen meerdere bodygraphs tegelijk gemount zijn.
 */
export function buildHdMonopoleLayerMarkup(geometry: HdMonopoleLayerGeometry, uid: number): string {
  const { gCenter, head, ajna } = geometry;

  const gCenterX = gCenter.x + gCenter.width / 2;
  const iconSide = gCenter.width * 0.3;
  // In het midden van de G-diamant: daar bedekt het icoon geen poortnummers;
  // de hover-tooltip benoemt de eigenlijke zetel (poort 2).
  const iconCenterY = gCenter.y + gCenter.height / 2;
  const iconTopY = iconCenterY - iconSide / 2;

  const headCenterX = head.x + head.width / 2;
  const beamTopY = head.y + head.height * 0.08;
  const beamBottomHalfWidth = iconSide * 0.1;
  const beamTopHalfWidth = head.width * 0.16;
  const beamGradientId = `hd-monopole-beam-grad-${uid}`;
  const beamEdgeGradientId = `hd-monopole-beam-edge-grad-${uid}`;
  const beamSoftFilterId = `hd-monopole-beam-soft-${uid}`;
  const beamCoreFilterId = `hd-monopole-beam-core-${uid}`;

  const beamQuad = (halfWidthBottom: number, halfWidthTop: number): string => points([
    [gCenterX - halfWidthBottom, iconTopY],
    [gCenterX + halfWidthBottom, iconTopY],
    [headCenterX + halfWidthTop, beamTopY],
    [headCenterX - halfWidthTop, beamTopY],
  ]);

  // Randlijn langs beide schuine zijden van de spies (crisp, geen blur).
  const beamEdgePath =
    `M${fmt(gCenterX - beamBottomHalfWidth)},${fmt(iconTopY)} L${fmt(headCenterX - beamTopHalfWidth)},${fmt(beamTopY)}`
    + ` M${fmt(gCenterX + beamBottomHalfWidth)},${fmt(iconTopY)} L${fmt(headCenterX + beamTopHalfWidth)},${fmt(beamTopY)}`;

  const personalityHeight = head.height * 0.34;
  const personalityWidth = personalityHeight * 0.62;
  const designHeight = ajna.height * 0.28;
  const designWidth = designHeight * 0.62;

  return `<g id="${HD_MONOPOLE_LAYER_ID}" class="hd-monopole-layer" pointer-events="none" aria-hidden="true">`
    + `<defs>`
    + `<linearGradient id="${beamGradientId}" gradientUnits="userSpaceOnUse" x1="${fmt(gCenterX)}" y1="${fmt(iconTopY)}" x2="${fmt(headCenterX)}" y2="${fmt(beamTopY)}">`
    + `<stop offset="0" stop-color="${BEAM_COLOR_BASE}" stop-opacity="0.5"/>`
    + `<stop offset="0.9" stop-color="${BEAM_COLOR_TIP}" stop-opacity="0.45"/>`
    + `<stop offset="1" stop-color="${BEAM_COLOR_TIP}" stop-opacity="0"/>`
    + `</linearGradient>`
    + `<linearGradient id="${beamEdgeGradientId}" gradientUnits="userSpaceOnUse" x1="${fmt(gCenterX)}" y1="${fmt(iconTopY)}" x2="${fmt(headCenterX)}" y2="${fmt(beamTopY)}">`
    + `<stop offset="0" stop-color="${BEAM_COLOR_EDGE}" stop-opacity="0.65"/>`
    + `<stop offset="0.9" stop-color="${BEAM_COLOR_EDGE}" stop-opacity="0.55"/>`
    + `<stop offset="1" stop-color="${BEAM_COLOR_EDGE}" stop-opacity="0"/>`
    + `</linearGradient>`
    + `<filter id="${beamSoftFilterId}" x="-50%" y="-10%" width="200%" height="120%"><feGaussianBlur stdDeviation="${fmt(iconSide * 0.02)}"/></filter>`
    + `<filter id="${beamCoreFilterId}" x="-50%" y="-10%" width="200%" height="120%"><feGaussianBlur stdDeviation="${fmt(iconSide * 0.008)}"/></filter>`
    + `</defs>`
    + `<polygon class="hd-monopole-beam" points="${beamQuad(beamBottomHalfWidth, beamTopHalfWidth)}" fill="url(#${beamGradientId})" filter="url(#${beamSoftFilterId})"/>`
    + `<polygon class="hd-monopole-beam-core" points="${beamQuad(beamBottomHalfWidth * 0.45, beamTopHalfWidth * 0.35)}" fill="url(#${beamGradientId})" filter="url(#${beamCoreFilterId})" opacity="0.9"/>`
    + `<path class="hd-monopole-beam-edge" d="${beamEdgePath}" fill="none" stroke="url(#${beamEdgeGradientId})" stroke-width="1.6"/>`
    + buildMonopoleIcon(gCenterX, iconCenterY, iconSide, uid)
    + buildCrystal(
      ajna.x + ajna.width / 2,
      // In het midden van de ajna, net onder de bovenste poortnummer-rij.
      ajna.y + ajna.height * 0.45,
      designWidth,
      designHeight,
      DESIGN_CRYSTAL_PALETTE,
      'design',
    )
    + buildCrystal(
      headCenterX,
      // Op het bovenste puntje: het kristal steekt een kwart boven de apex uit.
      head.y + personalityHeight * 0.25,
      personalityWidth,
      personalityHeight,
      PERSONALITY_CRYSTAL_PALETTE,
      'personality',
    )
    + `</g>`;
}
